import { EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import ConfirmButtons, { ButtonSizes } from '@zeus/core/buttons/ConfirmButtons';
import WorkbenchLink from '@zeus/core/WorkbenchLink';
import MarkdownEditor, { HIDE_SIDE_BY_SIDE_OPTIONS } from '_/common/components/MarkdownEditor';
import useToggleValue from '_/hooks/useToggleValue';
import { Card, Button, Tooltip } from 'antd';
import Markdown from 'markdown-to-jsx';
import React, { ReactElement, useState } from 'react';

import classes from './RunbookTextEditor.module.css';

const defaultText = 'A readme does not exist for this alarm. Click the icon to add text.';

interface IRunbookTextEditorProps {
  access: boolean;
  href: string;
  id: string;
  name: string;
  onSaveClick: (id: string, value: string) => void;
  text?: string;
}

function RunbookTextEditor({ access, href, id, name, onSaveClick, text = '' }: IRunbookTextEditorProps): ReactElement {
  const [isEditing, toggleEditing] = useToggleValue();
  const [textValue, setTextValue] = useState(text);
  const isSaveDisabled = !textValue?.length || textValue === text;

  const handleCancel = () => {
    setTextValue(text);
    toggleEditing();
  };

  const handleSave = async () => {
    await onSaveClick(id, textValue);
    toggleEditing();
  };

  return (
    // TODO: cardWidth doesn't exist in classes
    // <div className={classes.cardWidth}>
    <div>
      {isEditing && access ? (
        <Card
          styles={{ body: { padding: 0 } }}
          className={classes.marginTop}
          actions={[
            <div className={classes.editButtonsRow}>
              <ConfirmButtons
                className={classes.button}
                confirmText="Save"
                disabled={isSaveDisabled}
                noWrapper
                onCancel={handleCancel}
                onConfirm={handleSave}
                size={ButtonSizes.small}
              />
            </div>,
          ]}
        >
          <div>
            <MarkdownEditor editorOptions={HIDE_SIDE_BY_SIDE_OPTIONS} onChange={setTextValue} value={text || `## ${name}`} />
          </div>
        </Card>
      ) : (
        <Card
          className={classes.cardMarginTop}
          extra={
            <Tooltip title={access ? 'Edit Runbook' : 'You do not have the right permissions to edit this runbook'}>
              <span>
                <Button
                  type="text"
                  shape="circle"
                  onClick={toggleEditing}
                  size="small"
                  disabled={!access}
                  aria-label="edit"
                  icon={<EditOutlined key="edit" />}
                />
              </span>
            </Tooltip>
          }
          title={
            <div className={classes.headerFlex}>
              <ExclamationCircleOutlined />
              <div className={classes.marginLeft}>
                {access ? <WorkbenchLink href={href}>{name}</WorkbenchLink> : <span>{name}</span>}
              </div>
            </div>
          }
        >
          <div className={classes.marginTop}>
            <Markdown>{text || defaultText}</Markdown>
          </div>
        </Card>
      )}
    </div>
  );
}

export default RunbookTextEditor;
